import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import EngineerLayout from './EngineerLayout';
import { db } from '../../firebase';
import { ref, onValue } from 'firebase/database';
import { 
    FaCalendarAlt, 
    FaArrowLeft, 
    FaEye,
    FaSort
} from 'react-icons/fa';

const EngineerSubheInterestedWorksByDate = () => {
    const navigate = useNavigate();
    const [lotteryDate, setLotteryDate] = useState('');
    const [department, setDepartment] = useState('subhe');
    const [taluka, setTaluka] = useState('');
    const [works, setWorks] = useState([]);
    const [registrations, setRegistrations] = useState([]);
    const [meetings, setMeetings] = useState([]);
    const [selectedFilters, setSelectedFilters] = useState({ date: '', dept: 'subhe', taluka: '' });
    const [appliedDate, setAppliedDate] = useState('');
    const [loading, setLoading] = useState(true);

    // Marathi numerals for Sr.No.
    const marathiNumerals = ['०', '१', '२', '३', '४', '५', '६', '७', '८', '९'];
    const toMarathiNumber = (n) => String(n).split('').map(digit => marathiNumerals[parseInt(digit)] || digit).join('');

    useEffect(() => {
        setLoading(true);
        let engData = {}, socData = {};
        let engDone = false, socDone = false;

        const checkDone = () => {
            if (engDone && socDone) {
                const allRegs = [
                    ...Object.entries(engData).map(([key, val]) => ({ ...val, _key: key })),
                    ...Object.entries(socData).map(([key, val]) => ({ ...val, _key: key }))
                ];
                setRegistrations(allRegs);
                setLoading(false);
            }
        };

        // Fetch Meetings for dropdown from generatework
        const generateWorkRef = ref(db, 'ekamwatapzp/generatework');
        const unsubMeetings = onValue(generateWorkRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                const dateList = Object.keys(data).map(dateKey => ({
                    id: dateKey,
                    date: dateKey
                }));
                setMeetings(dateList.reverse());
            } else {
                setMeetings([]);
            }
        });

        // Fetch Works based on appliedDate
        let worksRef;
        if (appliedDate) {
            worksRef = ref(db, `ekamwatapzp/generatework/${appliedDate}`);
        } else {
            worksRef = ref(db, 'ekamwatapzp/WorkNames');
        }

        const unsubWorks = onValue(worksRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                let flattenedWorks = [];
                Object.entries(data).forEach(([key, val]) => {
                    if (val && typeof val === 'object') {
                        if (val.schemeName) {
                            flattenedWorks.push({ id: key, meetingNumber: val.meetingNumber || "General", ...val });
                        } else {
                            Object.entries(val).forEach(([id, value]) => {
                                flattenedWorks.push({ id, meetingNumber: key, ...value });
                            });
                        }
                    }
                });
                setWorks(flattenedWorks);
            } else {
                setWorks([]);
            }
        });

        // Fetch Registrations
        const engRef = ref(db, 'ekamwatapzp/EngineerRegistrations');
        const socRef = ref(db, 'ekamwatapzp/SocietyRegistrations');

        const unsubEng = onValue(engRef, (snapshot) => {
            engData = snapshot.val() || {};
            engDone = true;
            checkDone();
        });

        const unsubSoc = onValue(socRef, (snapshot) => {
            socData = snapshot.val() || {};
            socDone = true;
            checkRegDone();
        });

        const checkRegDone = () => {
             // Consistency fix for soc callback
             checkDone();
        };

        return () => {
            unsubMeetings();
            unsubWorks();
            unsubEng();
            unsubSoc();
        };
    }, [appliedDate]);

    const talukas = [
        "मिरज", "जत", "खानापूर (विटा)", "वाळवा (इस्लामपूर)", "तासगाव", 
        "शिराळा", "कडेगाव", "खानापूर", "पलूस", "कवठे महांकाळ"
    ];

    const handleSearch = () => {
        setAppliedDate(lotteryDate);
        setSelectedFilters(prev => ({ ...prev, dept: department, taluka: taluka }));
    };

    const filteredWorks = useMemo(() => {
        const deptMap = { 'subhe': 'सुबे', 'society': 'मजूर' };
        return works.filter(work => {
            const deptMatch = !selectedFilters.dept || work.sharedwork === deptMap[selectedFilters.dept];
            const talukaMatch = !selectedFilters.taluka || work.taluka === selectedFilters.taluka;
            return deptMatch && talukaMatch;
        });
    }, [works, selectedFilters.dept, selectedFilters.taluka]);

    // Pre-calculate work interest counts from registrations for O(1) lookup
    const workInterestMap = useMemo(() => {
        const counts = {};
        registrations.forEach(reg => {
            if (reg.work_payment) {
                Object.entries(reg.work_payment).forEach(([meetingNum, paymentData]) => {
                    const selected = paymentData.selectedWorks || [];
                    selected.forEach(workStr => {
                        const workNum = workStr.split(' - ')[0];
                        const key = `${meetingNum}_${workNum}`;
                        counts[key] = (counts[key] || 0) + 1;
                    });
                });
            }
        });
        return counts;
    }, [registrations]);

    const displayData = useMemo(() => {
        return filteredWorks.map(work => ({
            ...work,
            interestedCount: workInterestMap[`${work.meetingNumber}_${work.workNumber}`] || 0
        }));
    }, [filteredWorks, workInterestMap]);

    return (
        <EngineerLayout>
            <div className="p-8 bg-[#f4f7fe] min-h-screen font-marathi">
                {/* Header Section */}
                <div className="flex justify-between items-center mb-8 px-2">
                    <div className="flex items-center gap-4">
                        <button 
                            onClick={() => navigate('/engineer/subhe-interested-works-menu')}
                            className="p-2 bg-white rounded-full shadow-sm hover:bg-gray-50 transition text-gray-600 border border-gray-100 transition-transform active:scale-90"
                        >
                            <FaArrowLeft size={16} />
                        </button>
                        <h1 className="text-xl font-bold text-gray-800 tracking-tight">तारखेनुसार इंटरेस्टेड कामांची यादी पहा</h1>
                    </div>
                    <div className="text-xs font-medium text-gray-400">
                        जिल्हा परिषद / <span className="text-gray-500 font-bold tracking-tight">सुभे इंटरेस्टेड कामांची यादी पहा</span>
                    </div>
                </div>

                {/* Filter Card */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 mb-8">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-end">
                        <div className="space-y-2">
                            <label className="text-[13px] font-bold text-gray-500">लॉटरीची तारीख निवडा <span className="text-red-500">*</span></label>
                            <select 
                                className="w-full border border-gray-100 rounded-xl px-4 py-3 outline-none focus:border-blue-500 bg-gray-50/50 text-gray-700 font-bold transition-all"
                                value={lotteryDate}
                                onChange={(e) => setLotteryDate(e.target.value)}
                            >
                                <option value="">तारीख निवडा</option>
                                {meetings.map((m) => (
                                    <option key={m.id} value={m.id}>{m.date}</option>
                                ))}
                            </select>
                        </div>
                        <div className="space-y-2">
                            <label className="text-[13px] font-bold text-gray-500">विभाग</label>
                            <select 
                                className="w-full border border-gray-100 rounded-xl px-4 py-3 outline-none focus:border-blue-500 bg-gray-50/50 text-gray-700 font-bold transition-all"
                                value={department}
                                onChange={(e) => setDepartment(e.target.value)}
                            >
                                <option value="">विभाग निवड</option>
                                <option value="subhe">सु. बे.</option>
                                <option value="society">समाज</option>
                            </select>
                        </div>
                        <div className="space-y-2">
                            <label className="text-[13px] font-bold text-gray-500">तालुका निवडा</label>
                            <select 
                                className="w-full border border-gray-100 rounded-xl px-4 py-3 outline-none focus:border-blue-500 bg-gray-50/50 text-gray-700 font-bold transition-all"
                                value={taluka}
                                onChange={(e) => setTaluka(e.target.value)}
                            >
                                <option value="">तालुका निवडा</option>
                                {talukas.map((t, idx) => (
                                    <option key={idx} value={t}>{t}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <button 
                                onClick={handleSearch}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-10 py-3 rounded-xl font-bold shadow-lg shadow-blue-500/20 transition transform active:scale-95 flex items-center justify-center gap-2"
                            >
                                शोध
                            </button>
                        </div>
                    </div>

                    <div className="mt-10 overflow-hidden border border-gray-100 rounded-2xl shadow-sm">
                        {loading ? (
                            <div className="p-20 flex flex-col items-center justify-center space-y-4">
                                <div className="animate-spin text-blue-600 text-4xl">
                                    <FaSort />
                                </div>
                                <p className="text-gray-400 font-bold animate-pulse uppercase tracking-widest text-xs">माहिती लोड होत आहे...</p>
                            </div>
                        ) : (
                            <table className="w-full border-collapse">
                                <thead>
                                    <tr className="bg-white border-b border-gray-200 text-[12px] font-black text-gray-800 text-left uppercase">
                                        <th className="px-6 py-5 border-r border-gray-100 w-16">#</th>
                                        <th className="px-6 py-5 border-r border-gray-100 min-w-[400px]">कामाचे नाव</th>
                                        <th className="px-6 py-5 border-r border-gray-100 min-w-[180px]">निविदा रक्कम</th>
                                        <th className="px-6 py-5 min-w-[250px]">इंटरेस्टेड काउंट</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {displayData.length > 0 ? displayData.map((item, index) => (
                                        <tr key={item.id} className="hover:bg-blue-50/20 group transition-colors border-b border-gray-100 last:border-0 text-[14px] text-gray-700 font-bold">
                                            <td className="px-6 py-6 border-r border-gray-100 text-gray-400">{toMarathiNumber(index + 1)}</td>
                                            <td className="px-6 py-6 border-r border-gray-100 font-marathi leading-relaxed">{item.workName}</td>
                                            <td className="px-6 py-6 border-r border-gray-100 font-marathi">{item.tenderAmount}</td>
                                            <td className="px-6 py-6">
                                                <div className="flex items-center gap-3">
                                                    <span className="text-gray-800 font-marathi">{toMarathiNumber(item.interestedCount)}</span>
                                                    <span className="text-gray-300">-</span>
                                                    <button 
                                                        onClick={() => navigate(`/engineer/subhe-interested-works-detail/${item.id}`)}
                                                        className="text-blue-600 hover:text-blue-800 transition-colors flex items-center gap-2 group/link"
                                                    >
                                                        <FaEye className="text-blue-400 group-hover/link:scale-110 transition-transform" />
                                                        <span className="font-marathi underline decoration-blue-200 underline-offset-4">
                                                            इंटरेस्टेड ॲप्लिकेशन पहा
                                                        </span>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    )) : (
                                        <tr>
                                            <td colSpan="4" className="px-6 py-20 text-center text-gray-400 font-bold">
                                                निवडलेल्या निकषांनुसार कोणतीही माहिती उपलब्ध नाही.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>

            <style>{`
                .font-marathi {
                    font-family: 'Hind', sans-serif;
                }
                @import url('https://fonts.googleapis.com/css2?family=Hind:wght@400;500;600;700&display=swap');
            `}</style>
        </EngineerLayout>
    );
};

export default EngineerSubheInterestedWorksByDate;
